import { useMemo, useState } from 'react';
import {
  Dialog,
  FlexLayout,
  DialogType,
  StandardButton,
} from '@gv/triage-components';

import { CallTranscription } from 'types';
import { casesApi } from 'store/api/cases';
import { useDetailsQuery } from 'store/api/hospitals';
import {
  useTriggerTranscriptionMutation,
  useTriggerCaseTranscriptionMutation,
} from 'store/api/hospitals';

import * as Styles from './styles';

interface CallTranscriptionsProps {
  caseId?: string;
  hospitalId?: number;
  transcriptions?: CallTranscription[];
}

const formatSpeakerLabel = (speaker: string): string => {
  // Convert "spk_0" to "Speaker 1", "spk_1" to "Speaker 2", etc.
  const match = speaker.match(/^spk_(\d+)$/);
  if (match) {
    const speakerNumber = parseInt(match[1], 10) + 1;
    return `Speaker ${speakerNumber}`;
  }
  return speaker;
};

const getSpeakerColor = (speaker: string): string => {
  const colors: Record<string, string> = {
    spk_0: '#1976d2',
    spk_1: '#2e7d32',
    spk_2: '#ed6c02',
    spk_3: '#9c27b0',
    spk_4: '#d32f2f',
    spk_5: '#00796b',
    spk_6: '#f57c00',
    spk_7: '#5e35b1',
  };
  return colors[speaker] || '#757575';
};

const formatTimestamp = (seconds: number | undefined): string => {
  if (seconds === undefined || seconds === null || isNaN(seconds)) {
    return '0:00';
  }
  const minutes = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

export const CallTranscriptions = ({
  caseId,
  hospitalId,
  transcriptions,
}: CallTranscriptionsProps): JSX.Element | null => {
  const [open, setOpen] = useState(false);
  const [triggerTranscription, { isLoading: isLoadingById }] =
    useTriggerTranscriptionMutation();
  const [triggerCaseTranscription, { isLoading: isLoadingByCase }] =
    useTriggerCaseTranscriptionMutation();
  const [refetchCaseDetails] = casesApi.useLazyDetailsQuery();

  const { data: hospital } = useDetailsQuery(hospitalId?.toString() || '', {
    skip: !hospitalId,
  });

  const isLoading = isLoadingById || isLoadingByCase;
  const isTranscriptionEnabled =
    (hospital as any)?.auto_transcription_enabled === true;

  const completedTranscriptions = useMemo(() => {
    if (!transcriptions?.length) {
      return [];
    }
    return transcriptions.filter((t) => t.transcription_status === 'completed');
  }, [transcriptions]);

  const firstTranscription = useMemo(() => {
    if (!transcriptions?.length) {
      return null;
    }
    return transcriptions[0];
  }, [transcriptions]);

  const transcriptionStatus = firstTranscription?.transcription_status;
  const transcriptionId = firstTranscription?.id;

  const hasCompletedTranscriptions = completedTranscriptions.length > 0;
  const isProcessing = transcriptionStatus === 'processing';
  const isFailed = transcriptionStatus === 'failed';
  const isQueued = transcriptionStatus === 'queued';
  const isCompleted = transcriptionStatus === 'completed';

  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleTriggerTranscription = async () => {
    if (!caseId) {
      return;
    }
    setErrorMessage('');
    try {
      if (transcriptionId) {
        await triggerTranscription(transcriptionId).unwrap();
      } else {
        await triggerCaseTranscription(caseId).unwrap();
      }
      await refetchCaseDetails({ id: caseId });
      setOpen(false);
    } catch (err: any) {
      console.error('Failed to trigger transcription:', err);
      const message =
        err?.data?.message ||
        err?.data?.error ||
        'Failed to start transcription. Please try again.';
      setErrorMessage(message);
    }
  };

  const getButtonText = () => {
    if (isLoading) {
      return 'Starting Transcription...';
    }
    if (isProcessing) {
      return 'Transcription In Progress...';
    }
    if (isCompleted) {
      return 'Transcription Complete';
    }
    if (isFailed) {
      return 'Retry Transcription';
    }
    if (isQueued) {
      return 'Start Transcription';
    }
    if (!transcriptionId) {
      return 'Start Transcription';
    }
    return 'Start Transcription';
  };

  if (!caseId) {
    return null;
  }

  return (
    <>
      <Styles.ButtonContainer>
        <StandardButton
          text="Call Transcription"
          onClick={() => setOpen(true)}
        />
      </Styles.ButtonContainer>

      <Dialog
        open={open}
        width="90vw"
        maxWidth="1400px"
        title="Call Transcription"
        onClose={() => {
          setOpen(false);
          setErrorMessage('');
        }}
      >
        {(close, Text) => (
          <Text type={DialogType.Plain}>
            <Styles.ModalContent>
              {hasCompletedTranscriptions ? (
                <FlexLayout gap={16} flexDirection="column">
                  {completedTranscriptions.map((transcription) => (
                    <Styles.TranscriptionCard key={transcription.id}>
                      <Styles.TranscriptionMeta>
                        <span>Call ID: {transcription.outgoing_id}</span>
                        {transcription.duration_seconds && (
                          <span>
                            Duration:{' '}
                            {Math.floor(transcription.duration_seconds / 60)}:
                            {(transcription.duration_seconds % 60)
                              .toString()
                              .padStart(2, '0')}
                          </span>
                        )}
                        {transcription.speaker_count && (
                          <span>{transcription.speaker_count} speakers</span>
                        )}
                      </Styles.TranscriptionMeta>

                      {transcription.transcript_json?.medical_summary && (
                        <Styles.MedicalSummarySection>
                          <Styles.MedicalSummaryTitle>
                            Medical Summary
                          </Styles.MedicalSummaryTitle>
                          <Styles.MedicalSummaryText>
                            {transcription.transcript_json.medical_summary}
                          </Styles.MedicalSummaryText>
                        </Styles.MedicalSummarySection>
                      )}

                      <Styles.TranscriptionContent>
                        {transcription.transcript_json?.utterances ? (
                          <>
                            <Styles.TranscriptLabel>
                              Full Transcript
                            </Styles.TranscriptLabel>
                            {transcription.transcript_json.utterances.map(
                              (utterance, idx) => (
                                <Styles.Utterance key={idx}>
                                  <Styles.SpeakerLabel
                                    color={getSpeakerColor(utterance.speaker)}
                                  >
                                    {formatSpeakerLabel(utterance.speaker)}
                                    <Styles.Timestamp>
                                      {formatTimestamp(utterance.startTime)}
                                    </Styles.Timestamp>
                                  </Styles.SpeakerLabel>
                                  <Styles.UtteranceText>
                                    {utterance.text}
                                  </Styles.UtteranceText>
                                </Styles.Utterance>
                              )
                            )}
                          </>
                        ) : (
                          <Styles.TranscriptionText>
                            {transcription.transcript_text ||
                              'No transcript text available'}
                          </Styles.TranscriptionText>
                        )}
                      </Styles.TranscriptionContent>
                    </Styles.TranscriptionCard>
                  ))}
                </FlexLayout>
              ) : (
                <Styles.EmptyState>
                  <Styles.EmptyStateText>
                    {transcriptionId
                      ? 'No transcription available yet.'
                      : 'No recording available for this call.'}
                  </Styles.EmptyStateText>
                  <Styles.EmptyStateSubtext>
                    {transcriptionId
                      ? `Status: ${transcriptionStatus || 'Not started'}`
                      : 'Transcription requires a call recording.'}
                  </Styles.EmptyStateSubtext>
                  {!isTranscriptionEnabled && (
                    <Styles.EmptyStateSubtext
                      style={{ color: '#ff9800', marginTop: '8px' }}
                    >
                      Post-call transcription is disabled for this hospital.
                      Please enable it in hospital settings to use this feature.
                    </Styles.EmptyStateSubtext>
                  )}
                  {isTranscriptionEnabled && (isLoading || isProcessing) && (
                    <Styles.EmptyStateSubtext
                      style={{ color: '#2196f3', marginTop: '8px' }}
                    >
                      Transcription in progress. This may take a couple of
                      seconds. Please keep this window open.
                    </Styles.EmptyStateSubtext>
                  )}
                  <StandardButton
                    text={getButtonText()}
                    onClick={handleTriggerTranscription}
                    disabled={
                      isLoading ||
                      isProcessing ||
                      isCompleted ||
                      !isTranscriptionEnabled
                    }
                  />
                  {errorMessage && (
                    <Styles.ErrorText>{errorMessage}</Styles.ErrorText>
                  )}
                </Styles.EmptyState>
              )}
            </Styles.ModalContent>
          </Text>
        )}
      </Dialog>
    </>
  );
};
